import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';

import { token } from '@bp-files/ui';
import { drawnStrokes, smoothed, type RawPoint } from '@/components/Signature/signatureInk';
import type { PageView } from './signField';
import { SIGNING_COPY } from './signing.copy';

/**
 * The freehand drawing surface, over one page (FIL-8c, the pencil tool).
 *
 * **`SignaturePad`'s capture, on a PDF page instead of a fixed pad.** It owns
 * pointer events and pixels and no decisions — the ink model in
 * `signatureInk.ts` turns what it captures into a mark, exactly as the account
 * signature is drawn. What differs from the pad is only where it lives: an
 * absolutely-positioned canvas filling the page, so a stroke lands in the same
 * CSS-pixel space `strokesToInkWithBox` and `rectFromScreen` read to place it.
 *
 * **Unlike `FieldLayer`, this layer *is* `touch-none`.** A single finger has to
 * draw rather than scroll the column out from under the mark. That is only safe
 * because zoom is locked while the pencil is armed (`PdfViewer` withholds
 * `onPinch`) — so there is no pinch to share the page with, and moving around
 * means putting the pencil down. `FieldLayer`'s header explains why the placing
 * layer must never take the same line.
 *
 * **The parent holds the strokes; this seeds from them and lifts the lot.** The
 * committed strokes arrive once as a seed and are kept in a ref that this
 * component appends to and paints from — the pad's pattern — so a finished
 * stroke never flickers between "mine" and "the parent's". A page scrolled away
 * and back remounts fresh from the buffer `useSigning` still holds.
 */

/** Never draw more than two canvas pixels per CSS pixel. `SignaturePad`'s cap. */
const MAX_DEVICE_PIXEL_RATIO = 2;

export interface DrawLayerProps {
  pageIndex: number;
  view: PageView;
  /** The strokes already drawn on this page, in CSS pixels within the page. */
  strokes: RawPoint[][];
  /** Reports the page's strokes after each one is finished. `[]` clears them. */
  onStrokesChange: (pageIndex: number, strokes: RawPoint[][], view: PageView) => void;
}

export function DrawLayer({ pageIndex, view, strokes, onStrokesChange }: DrawLayerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  /** Seeded once from `strokes`; this component's source of truth thereafter. */
  const done = useRef<RawPoint[][]>(strokes);
  /** The stroke being drawn right now, or `null` between strokes. */
  const current = useRef<RawPoint[] | null>(null);

  /**
   * Repaint everything from the captured points.
   *
   * Redrawn rather than appended to, for `SignaturePad`'s reason: a point's
   * width depends on the one after it, so the last segment is not final until
   * the next arrives. jsdom has no 2D context, so this no-ops there — every
   * decision worth testing is in `signatureInk.ts`, not here.
   */
  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    const ratio = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;

    if (canvas.width !== Math.floor(cssWidth * ratio)) {
      canvas.width = Math.floor(cssWidth * ratio);
      canvas.height = Math.floor(cssHeight * ratio);
    }

    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, cssWidth, cssHeight);

    context.strokeStyle = token('bp-ink');
    context.lineCap = 'round';
    context.lineJoin = 'round';

    const live = current.current;
    const all = live !== null ? [...done.current, live] : done.current;

    for (const stroke of smoothed(drawnStrokes(all))) {
      for (let i = 1; i < stroke.length; i += 1) {
        const from = stroke[i - 1];
        const to = stroke[i];
        if (from === undefined || to === undefined) continue;

        context.beginPath();
        context.lineWidth = (from.w + to.w) / 2;
        context.moveTo(from.x, from.y);
        context.lineTo(to.x, to.y);
        context.stroke();
      }

      // A single tap is drawn as a dot, as the pad draws it.
      const only = stroke[0];
      if (stroke.length === 1 && only !== undefined) {
        context.beginPath();
        context.arc(only.x, only.y, only.w / 2, 0, Math.PI * 2);
        context.fillStyle = token('bp-ink');
        context.fill();
      }
    }
  }, []);

  // Paint the seeded strokes once the canvas is up.
  useEffect(() => {
    repaint();
  }, [repaint]);

  /**
   * A pinch anywhere on the stage drops the stroke in flight.
   *
   * Zoom is locked while the pencil is armed, so this should never fire — it is
   * `FieldLayer`'s belt-and-braces kept here too: every point is in a CSS-pixel
   * space a running zoom transform would make briefly untrue, and a mark stored
   * from it would be wrong. Abandoning is the safe answer.
   */
  useEffect(() => {
    if (view.gesturing !== true) return;
    current.current = null;
    repaint();
  }, [view.gesturing, repaint]);

  function pointFrom(event: ReactPointerEvent<HTMLCanvasElement>): RawPoint {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      // The event's own clock, so a busy frame does not read as a slow pen.
      t: event.timeStamp,
    };
  }

  /** jsdom implements no pointer capture, and it can throw in a browser too. */
  function capture(element: Element, pointerId: number): void {
    try {
      element.setPointerCapture(pointerId);
    } catch {
      // A stroke without capture ends at the canvas edge, which `onPointerLeave`
      // already does deliberately.
    }
  }

  function endStroke(): void {
    const stroke = current.current;
    current.current = null;
    if (stroke === null || stroke.length === 0) return;

    done.current = [...done.current, stroke];
    onStrokesChange(pageIndex, done.current, view);
    repaint();
  }

  return (
    <canvas
      ref={canvasRef}
      data-testid={`draw-layer-${String(pageIndex)}`}
      role="img"
      aria-label={SIGNING_COPY.draw.surface(pageIndex + 1)}
      // `touch-none` so a finger draws; see the header for why this layer may.
      className="absolute inset-0 h-full w-full touch-none"
      style={{ cursor: 'crosshair' }}
      onPointerDown={(event) => {
        capture(event.currentTarget, event.pointerId);
        current.current = [pointFrom(event)];
        repaint();
      }}
      onPointerMove={(event) => {
        if (current.current === null) return;
        current.current.push(pointFrom(event));
        repaint();
      }}
      onPointerUp={endStroke}
      onPointerCancel={() => {
        // The browser or the stage claimed the gesture. This was not a stroke.
        current.current = null;
        repaint();
      }}
      onPointerLeave={() => {
        // A pen that leaves the page mid-stroke ends it, rather than joining
        // wherever it comes back — which would draw a line across the mark.
        endStroke();
      }}
    />
  );
}
